// src/App.js
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";

import PromptChallengeDetail from './PromptChallengeDetail';
import BattlePage from './BattlePage';
import JoinChallengePage from './JoinChallengePage';

import { AuthProvider, useAuth } from "./AuthContext";
import { ThemeProvider } from "./ThemeContext";

import Header from "./auth-layout-pages/Header";
import LandingPage from "./landing-pages/LandingPage";
import ChallengesPage from "./ChallengesPage";
import ContactandSupport from "./contact-support-pages/ContactandSupport";
import PricingPage from "./PricingPage";
import HelpPage from "./contact-support-pages/HelpPage";
import Profile from "./auth-layout-pages/Profile";
import Footer from "./auth-layout-pages/Footer";

import SessionsPage    from './SessionsPage';
import BadgesPage      from './BadgesPage';
import LeaderboardPage from './LeaderboardPage';

import PrivacyPolicy from "./contact-support-pages/PrivacyPolicy";
import TermsOfService from "./contact-support-pages/TermsOfService";
import FAQ from "./contact-support-pages/FAQ";

import "./App.css";

// Gate: signed-in users see the page, signed-out users get the auth modal and bounce home
const Gate = ({ children }) => {
  const { user, authLoading, showAuthModal } = useAuth();

  useEffect(() => {
    if (!authLoading && !user) {
      showAuthModal("signup");
    }
  }, [authLoading, user, showAuthModal]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/" replace />;
  return children;
};

const AppLayout = () => {
  const location = useLocation();
  const { showAuthModal } = useAuth();

  const isChallengesPage =
    location.pathname.startsWith('/challenges') ||
    location.pathname.startsWith('/tasks');

  const hideChrome =
    location.pathname.startsWith('/challenge/') ||
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/battle') ||
    location.pathname.startsWith('/play/') ||
    location.pathname.startsWith('/leaderboard') ||
    isChallengesPage;

  return (
    <div className="app-container">
      {!hideChrome && <Header openAuthModal={showAuthModal} />}

      <main className="main-content">
        <Routes>
          <Route path="/" element={<LandingPage openAuthModal={showAuthModal} />} />

          <Route path="/challenges" element={<Gate><ChallengesPage isPremium={false} /></Gate>} />
          <Route path="/tasks"      element={<Gate><ChallengesPage isPremium={false} /></Gate>} />
          <Route path="/challenge/:language/:title" element={<PromptChallengeDetail />} />

          <Route path="/battle" element={<BattlePage />} />
          <Route path="/play/:code" element={<JoinChallengePage />} />

          <Route path="/leaderboard" element={<LeaderboardPage />} />

          <Route path="/profile"          element={<Profile />} />
          <Route path="/help-resources"   element={<HelpPage />} />
          <Route path="/privacy-policy"   element={<PrivacyPolicy />} />
          <Route path="/faq"              element={<FAQ />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/pricing"          element={<PricingPage />} />
          <Route path="/contact-support"  element={<ContactandSupport />} />

          {/* Dashboard routes */}
          <Route path="/dashboard"          element={<Navigate to="/dashboard/sessions" replace />} />
          <Route path="/dashboard/history"  element={<Navigate to="/dashboard/sessions" replace />} />
          <Route path="/dashboard/sessions" element={<SessionsPage />} />
          <Route path="/dashboard/badges"   element={<BadgesPage />} />
        </Routes>
      </main>

      {!hideChrome && <Footer />}
    </div>
  );
};

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <ThemeProvider>
          <AppLayout />
        </ThemeProvider>
      </Router>
    </AuthProvider>
  );
}